import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Modal,
  Grid,
  Card,
  CardContent,
  Button,
  Input,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import ContentCopyIcon from '../../../../../assets/svg/content_copy.svg?react';
import styles from './LibraryList.module.scss';
import { v4 as uuidv4 } from 'uuid';
import { LibraryListProps, LibraryItem } from './types';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../../../slices/documents/documentsSlice';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import { useLibraryFunctions } from '../../hooks/useLibraryFunctions';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { ROUTES } from '../../../../common/constants/routes';
import { useNavigate } from 'react-router';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import MenuBlock from '../../../../common/menu-block';
import VerticalDotsIcon from '../../../../../assets/svg/more_vert.svg?react';
import { useDeleteLibraryItemMessageModal } from '../../hooks/useDeleteMessageModal';
import ContractGeneratorIcon from '../../../../../assets/svg/contract-generator-icon.svg?react';
import {
  selectDisclaimerChecked,
  acceptDisclaimer,
} from '../../../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import DisclaimerText from '../../../../common/disclaimer-text/DisclaimerText';
import { LibraryListRow } from './LibraryListRow';
import { selectUser } from '../../../../slices/user/userSlice';
import { showMessage } from '../../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';

const LibraryList = ({ libraryItems, cardView, ownTemplate, category }: LibraryListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const company = useAppSelector(selectAllCompanies)[0];
  const navigate = useNavigate();
  const documentUploads = useAppSelector(selectAllDocuments);
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;
  const currentUser = useAppSelector(selectUser);
  const {
    handleS3DownloadLink,
    handleLibraryItemCopy,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    navigateToTemplatePage,
    disclaimerMessage,
    handleTemplateCopy,
    setDisclaimerMessage,
    libraryItemForGenerator,
    setLibraryItemForGenerator,
  } = useLibraryFunctions();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();

  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();

  const handleNavigate = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      return false;
    } else {
      if (freePlan) {
        if (actionAvailableFree || !item.region) {
          navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
        } else {
          dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
        }
      } else {
        navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
      }
    }
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow className={styles.header_row}>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.name')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.profiles')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.workflow')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.documentType')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.dateCreatedModified')}
          </Typography>
        </TableCell>
        <TableCell className={`${styles.header_cell} ${styles.actions_header}`}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.actions')}
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const CopyModal: React.FC<{ item: LibraryItem }> = ({ item }) => {
    const handleCopying = (newname: string) => {
      const data = {
        name: newname,
        category: item.category,
        dt_workflows: item.dl_workflows,
        dt_types: item.dl_types,
        user: currentUser.id,
      };

      try {
        handleLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
        setCopyModalOpen(null); // Close the modal after the operation finishes
      } catch (error) {
        console.error('Error copying library item:', error);
      }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newTemplateName = formData.get('newTemplateName') as string;
      if ('region' in item) {
        handleCopying(newTemplateName);
      } else {
        const updatedItem = { ...item, name: newTemplateName };
        try {
          handleTemplateCopy(
            updatedItem,
            currentUser.id,
            category === 'All' ? null : updatedItem.category
          );
          setCopyModalOpen(null);
        } catch (error) {
          console.error('Error copying template:', error);
        }
      }
    };
    return (
      <Modal
        open={copyModalOpen?.id === item.id}
        className={styles.copymodal}
        onClose={() => setCopyModalOpen(null)}
      >
        <Grid container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
          <Grid size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Copy to Own library
                </Typography>

                <Typography>Please change the template name if necessary.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Input
                      name='newTemplateName'
                      placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                      defaultValue={`${
                        item.name ? item.name : t('templatePage.headerDefault')
                      } - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                      className={styles.input}
                      fullWidth
                      required
                    />
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                    <Button
                      variant='contained'
                      onClick={() => setCopyModalOpen(null)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                    >
                      Confirm
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  const renderActionButtons = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    const templateMoreActions = [
      {
        name: t('buttons.view'),
        onClick: () => {
          handleNavigate(item);
        },
      },
      {
        name: t('buttons.edit'),
        onClick: () => {
          handleNavigate(item);
        },
      },
      {
        name: t('buttons.delete'),
        onClick: () => {
          setLibraryItemToDelete(item);
          setDeleteLibraryItemModalOpen(true);
        },
      },
    ];

    const handleGenerateContract = () => {
      const actionAvailableFree = 'is_free' in item && item.is_free;
      if ((freePlan && actionAvailableFree) || !freePlan || !('region' in item)) {
        if (disclaimerAccepted) {
          navigate(
            `${ROUTES.DOCUMENT_LIBRARY}/${item.region ? item.region : 'company-templates'}/${
              item.id
            }?generateDocument=true`
          );
        } else {
          setLibraryItemForGenerator(item);
          setDisclaimerMessage(true);
        }
      } else if (freePlan && !actionAvailableFree) {
        dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
      }
    };

    const handleDownloading = () => {
      if (!item.download_link) {
        setFileNameForDownloading(
          'name' in item
            ? item.name
            : documentUploads
                ?.find((el) => el.id === item.document_upload)
                ?.name?.replace('.pdf', '')
                .replace('.docx', '')
                .replace('.doc', '')
        );
        setLibraryItemForDownloading(item);
        setFileTypeModalOpen(true);
      } else {
        handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
      }
    };

    return (
      <Box className={styles.actions_container}>
        {!item.download_link && (
          <Tooltip title={t('buttons.generateContract')} placement='top' arrow>
            <IconButton
              size='small'
              onClick={() => handleGenerateContract()}
              className={styles.action_button}
            >
              <ContractGeneratorIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('buttons.download')} placement='top' arrow>
          <IconButton
            size='small'
            onClick={() => {
              if (freePlan || !item.region) {
                if (actionAvailableFree || !item.region) {
                  handleDownloading();
                } else {
                  dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                }
              } else {
                handleDownloading();
              }
            }}
            className={styles.action_button}
          >
            <FileSaveIcon />
          </IconButton>
        </Tooltip>
        {!item.download_link && (
          <Tooltip title={t('buttons.copyTemplate')} placement='top' arrow>
            <IconButton
              size='small'
              onClick={() => {
                if (freePlan || !item.region) {
                  if (actionAvailableFree || !item.region) {
                    setCopyModalOpen(item);
                  } else {
                    dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                  }
                } else {
                  setCopyModalOpen(item);
                }
              }}
              className={styles.action_button}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        )}
        {'document_upload' in item && (
          <MenuBlock options={templateMoreActions} moreIcon={<VerticalDotsIcon />} />
        )}
      </Box>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        className={styles.table_container}
        sx={{
          boxShadow: 'none',
          maxHeight: `calc(100vh - ${cardView ? '219px' : '179px'})`,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader>
          {renderTableHeader()}
          <TableBody>
            {libraryItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <p className={styles.empty_message}>
                    {t('emptyStateMessages.emptyLibraryMessage')}
                  </p>
                </TableCell>
              </TableRow>
            )}
            {libraryItems.map((item) => {
              return (
                <React.Fragment key={`${item.id}-${uuidv4()}`}>
                  <LibraryListRow item={item} renderActionButtons={renderActionButtons} />
                  <CopyModal item={item} />
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {planUpgradingMessage && (
        <MessageModal
          open={planUpgradingMessage}
          modalMessage={t('messages.considerStartingPlanLibrary')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanUpgradingMessage(false);
          }}
        />
      )}
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            handleTemplateDownload(
              libraryItemForDownloading.id,
              fileTypeForDownloading,
              fileNameForDownloading,
              ownTemplate ? true : false
            );
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={libraryItemToDelete}
          onClose={() => {
            handleDeleteDocumentTemplate();
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                navigate(
                  `${ROUTES.DOCUMENT_LIBRARY}/${
                    libraryItemForGenerator.region
                      ? libraryItemForGenerator.region
                      : 'company-templates'
                  }/${libraryItemForGenerator.id}?generateDocument=true`
                );
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
            setLibraryItemForGenerator(null);
          }}
        />
      )}
      {(downloadPendingStatus ||
        deletingLibraryItemPending ||
        disclaimerStatus === 'loadingAccepting') && <ProgressOverlay />}
    </>
  );
};

export default LibraryList;
